import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import PatientCard from "../../components/PatientCard";
import { useNavigate } from "react-router-dom";
import "./records.css";
import { ErrorModal } from "../../components/modals/ErrorModal";
import {
  appRatedAtom,
  loadableCompleteVerifiedRecordsAtom,
  refreshingAtom,
} from "../../jotai/atoms";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import RefreshButton from "../../components/buttons/RefreshButton";
import FAQButton from "../../components/buttons/FAQButton";
import { Squirrel } from "../../components/spinner/Squirrel";
import { USE_WHITELABEL_BRANDING, WHITELABEL_KEY } from "../../globals";
import { getAPIClient } from "../../apiClient";
import { fetchRecordsUntilUpdated } from "../../utils/polling";
import ReactDOMServer from "react-dom/server";
import { Spinner } from "../../components/spinner/Spinner";
import useFormFactor from "../../hooks/useFormFactor";
import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { InAppReview } from "@capacitor-community/in-app-review";
import PullToRefresh from "react-simple-pull-to-refresh";
import { vibrateDevice } from "../../utils/capacitor";

interface RecordsTranslations {
  searchMessage: string;
  searchButton: string;
  searchError: string;
  header: string;
  subtitle: string;
  updated: string;
}

interface RecordsState {
  searchUid?: string;
}

export function Records({ recordType }: { recordType: "immunization" | "screening" }) {
  const { state } = useLocation();
  const { formFactor } = useFormFactor();
  const { searchUid } = (state as RecordsState) || {};
  const [allVerifiedRecords] = useAtom(loadableCompleteVerifiedRecordsAtom);
  const [initialLoad, setInitialLoad] = useState(true);
  const [showErrorModal, setShowErrorModal] = useState<string | boolean>();
  const [cardOpen, setCardOpen] = useState<boolean[]>([]);
  const [refreshing, setRefreshing] = useAtom(refreshingAtom);
  const [appRated, setAppRated] = useAtom(appRatedAtom);
  const filteredRecords = React.useMemo(() => {
    if (allVerifiedRecords.state !== "hasData") {
      return [];
    }

    if (recordType === "immunization") {
      return allVerifiedRecords.data;
    }

    let filteredRecords = allVerifiedRecords.data;
    // This is because for NJ health launch, we want only those test results from NJ
    if (USE_WHITELABEL_BRANDING && WHITELABEL_KEY === "nj") {
      filteredRecords = filteredRecords.filter(
        (record) => record.search.izProviderKey.toLowerCase() === "nj"
      );
    }

    // In Mobile, users can navigate to the test results view with only the relevant search displayed
    if (searchUid) {
      filteredRecords = filteredRecords.filter((r) => r.search.uid === searchUid);
    }

    return filteredRecords;
  }, [allVerifiedRecords, searchUid]);

  // This will scroll down to the selected record and open it if the index is appended to the url
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && filteredRecords.length > 0) {
      const index = parseInt(hash.substring(1));
      if (!isNaN(index) && index >= 0 && index < filteredRecords.length) {
        const isMobile = window.innerWidth < 768;
        setTimeout(
          () => {
            toggleCardOpen(index);
            const element = document.getElementById(index.toString());
            if (element) {
              element.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
          },
          isMobile ? 300 : 100
        );
      }
    }
  }, [filteredRecords.length]);

  const rateTheApp = async () => {
    if (Capacitor.getPlatform() === "web" || appRated) return;

    await setAppRated(true);
    const { value } = await Dialog.confirm({
      title: "Rate Docket",
      message: "Would you like to give Docket a review?",
    });

    if (value) {
      await InAppReview.requestReview();
    }
  };

  useEffect(() => {
    // Do not want to refresh infinitely if reloading in the middle of a refresh
    setRefreshing(false);
  }, []);

  useEffect(() => {
    if (filteredRecords.length > 0) {
      const initialOpen = Array(filteredRecords.length).fill(false);
      initialOpen[0] = true;
      setCardOpen(initialOpen);

      if (!appRated) {
        rateTheApp();
      }
    }
  }, [JSON.stringify(filteredRecords.map((r) => r.record.uid))]);

  // Only 1 card can be open at a time
  const toggleCardOpen = (index: number) => {
    const prevState = cardOpen[index];
    const newOpen = Array(filteredRecords.length).fill(false);
    newOpen[index] = !prevState;
    setCardOpen(newOpen);
  };

  const navigate = useNavigate();
  const { t } = useTranslation();

  const translation = useMemo<RecordsTranslations>(() => {
    return {
      searchMessage:
        recordType === "immunization"
          ? t("immunizations.search_records_message")
          : t("screening.search_screenings_message"),
      searchButton:
        recordType === "immunization"
          ? t("immunizations.immunizations_add_records_button")
          : t("screening.search_screenings_button"),
      searchError:
        recordType === "immunization"
          ? t("immunizations.search_records_error")
          : t("screening.search_records_error"),
      header:
        recordType === "immunization"
          ? t("immunizations.records_header")
          : t("screening.records_header"),
      subtitle:
        recordType === "immunization"
          ? formFactor === "mobile"
            ? t("immunizations.records_subtitle_mobile")
            : t("immunizations.records_subtitle")
          : formFactor === "mobile"
          ? t("screening.records_subtitle_mobile")
          : t("screening.records_subtitle"),
      updated: t("immunizations.updated"),
    };
  }, [recordType, formFactor]);

  useEffect(() => {
    if (allVerifiedRecords.state === "hasData") {
      setInitialLoad(false);
    }
  }, [allVerifiedRecords.state]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await vibrateDevice();
    await getAPIClient().refreshRecords();
    await fetchRecordsUntilUpdated(true);
    setRefreshing(false);
  };

  const noPatients = () => {
    return (
      <div className="flex px-6 flex-col gap-2 max-md:mt-12" data-testid="no-patients">
        <h2 className="text-black dark:text-white text-md font-black mobile-text-color mx-auto">
          {translation.searchMessage}
        </h2>
        <button
          className="btn docket-button-responsive md:w-fit max-md:w-full mx-auto"
          onClick={() => navigate("/home/search/newsearch")}
        >
          {translation.searchButton}
        </button>
      </div>
    );
  };

  const withPatients = () => {
    if (allVerifiedRecords.state !== "hasData") return null;
    const lastUpdatedTime: Date | null = allVerifiedRecords.data.reduce<Date | null>(
      (maxUpdatedDate, cur) => {
        if (!cur.record.updatedAt) {
          return maxUpdatedDate || null;
        }

        const currentUpdatedDate = new Date(cur.record.updatedAt);
        if (isNaN(currentUpdatedDate.getTime())) {
          return maxUpdatedDate;
        }

        return maxUpdatedDate === null || maxUpdatedDate < currentUpdatedDate
          ? currentUpdatedDate
          : maxUpdatedDate;
      },
      null
    );

    const recordsContent = (
      <div className="max-md:flex max-md:flex-col max-md:gap-2">
        <div className="flex md:gap-1 md:flex-wrap max-md:px-6">
          <div className="flex flex-col max-md:items-center max-md:w-full">
            <p className="text-dark dark:text-white text-lg font-bold max-md:text-white mobile-text-color">
              {translation.header}
            </p>
            <p className="text-dark dark:text-white text-md max-md:text-center max-md:text-sm max-md:text-dark-blue max-md:dark:text-white max-md:font-semibold">
              {translation.subtitle}
            </p>
            {lastUpdatedTime && (
              <p className="text-dark dark:text-white md:text-lg max-md:text-sm max-md:text-dark-blue max-md:dark:text-white max-md:font-semibold">
                {translation.updated}
                <span data-testid="update-time">
                  {DateTime.fromJSDate(lastUpdatedTime).toLocaleString()}
                </span>
              </p>
            )}
          </div>
          <div className="flex gap-1 ml-auto max-md:hidden">
            <RefreshButton />
            <FAQButton />
          </div>
        </div>
        <div className="separater max-md:hidden"></div>
        <div className="flex flex-col">
          {filteredRecords.map((r, index) => (
            <div id={index.toString()} key={index}>
              <PatientCard
                completeRecord={r}
                index={index}
                open={cardOpen[index] ?? false}
                onOpenToggle={() => toggleCardOpen(index)}
                screening={recordType === "screening"}
                hideHeader={!!searchUid}
              />
            </div>
          ))}
        </div>
      </div>
    );

    return (
      <>
        <div data-testid="desktop-records-list" className="max-md:hidden">
          {recordsContent}
        </div>
        <div data-testid="mobile-records-list" className="md:hidden">
          <PullToRefresh
            onRefresh={handleRefresh}
            refreshingContent={<Spinner />}
            pullingContent={<></>}
            className="pb-20"
            pullDownThreshold={78}
            maxPullDownDistance={120}
          >
            {recordsContent}
          </PullToRefresh>
        </div>
      </>
    );
  };

  if (allVerifiedRecords.state === "hasError") {
    setShowErrorModal(translation.searchError);
  }

  return (
    <>
      <div className="flex-col max-md:pb-20 max-md:pt-6">
        {showErrorModal && (
          <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
        )}
        {(allVerifiedRecords.state === "loading" || initialLoad || refreshing) && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <Squirrel />
          </div>
        )}
        <div className="column is-gapless">
          {allVerifiedRecords.state === "hasData" &&
          !initialLoad &&
          allVerifiedRecords.data.length > 0
            ? withPatients()
            : noPatients()}
        </div>
      </div>
    </>
  );
}

export default Records;
